import { useParams, Link } from 'react-router-dom';
import { projectList } from '@/mocks/projects';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import NotFound from '@/pages/NotFound';

const PortfolioDetailPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const project = projectList.find(p => p.slug === slug);

  if (!project) {
    return <NotFound />;
  }

  const projectJsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      "name": project.title,
      "description": project.fullDescription,
      "url": `https://bitninjatech.com/portfolio/${project.slug}`,
      "image": project.image,
      "about": project.category,
      "creator": {
        "@type": "Organization",
        "name": "BitNinja Technologies",
        "url": "https://bitninjatech.com"
      },
      "keywords": project.techStack.join(', ')
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://bitninjatech.com" },
        { "@type": "ListItem", "position": 2, "name": "Portfolio", "item": "https://bitninjatech.com/portfolio" },
        { "@type": "ListItem", "position": 3, "name": project.title, "item": `https://bitninjatech.com/portfolio/${project.slug}` }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <SeoHead
        title={`${project.title} | Portfolio | BitNinja Technologies`}
        description={`${project.title} — ${project.description}. Client: ${project.client}. Category: ${project.category}. Built by BitNinja Technologies.`}
        canonicalUrl={`https://bitninjatech.com/portfolio/${project.slug}`}
        ogImage={project.image}
        jsonLd={projectJsonLd}
      />
      <Navbar />
      <section className="pt-28 md:pt-32 pb-16 md:pb-24">
        <div className="section-padding">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/portfolio"
              className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-foreground-900 transition-colors mb-8 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Portfolio
            </Link>

            <div className="flex items-center gap-3 mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-500/10 text-primary-500 text-[10px] font-mono uppercase tracking-widest">
                {project.category}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-foreground-500">
                {project.client} &middot; {project.date}
              </span>
            </div>

            <h1 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.02] tracking-tight text-foreground-900 mb-6">
              {project.title}
            </h1>

            <div className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden mb-10">
              <img
                alt={project.title}
                className="w-full h-full object-cover object-center"
                src={project.image}
              />
            </div>

            <div className="grid lg:grid-cols-3 gap-10">
              <div className="lg:col-span-2">
                <h2 className="font-display text-2xl font-bold text-foreground-900 mb-5">Project Overview</h2>
                <p className="text-[15px] leading-relaxed text-foreground-700 mb-6">
                  {project.fullDescription}
                </p>

                <h2 className="font-display text-2xl font-bold text-foreground-900 mb-5">Key Results</h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  {project.results.map((result, index) => (
                    <div key={index} className="flex items-start gap-3 p-4 bg-background-100 rounded-xl border border-background-200">
                      <span className="w-6 h-6 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-check-line text-primary-500 text-sm"></i>
                      </span>
                      <span className="text-sm text-foreground-800 font-medium">{result}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="bg-background-100 rounded-2xl p-6 border border-background-200 sticky top-28">
                  <h3 className="font-display text-lg font-bold text-foreground-900 mb-4">Tech Stack</h3>
                  <div className="flex flex-wrap gap-2 mb-6">
                    {project.techStack.map((tech) => (
                      <span
                        key={tech}
                        className="inline-flex items-center px-3 py-1.5 rounded-full bg-background-50 border border-background-300 text-xs font-mono text-foreground-700"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="border-t border-background-200 pt-4 space-y-3">
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-1">Client</div>
                      <div className="text-sm font-medium text-foreground-900">{project.client}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-1">Category</div>
                      <div className="text-sm font-medium text-foreground-900">{project.category}</div>
                    </div>
                    <div>
                      <div className="font-mono text-[10px] uppercase tracking-widest text-foreground-500 mb-1">Completed</div>
                      <div className="text-sm font-medium text-foreground-900">{project.date}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-background-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                View All Projects
              </Link>
              <Link
                to="/#contact"
                className="inline-flex items-center gap-2 bg-foreground-900 hover:bg-foreground-800 text-background-50 px-6 py-3 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                Start a Similar Project
                <i className="ri-arrow-right-up-line text-base"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PortfolioDetailPage;